import dayjs from 'dayjs';
import {
  allocateFefo, lotsAt, nextCode, type DocLine, type DocType, type Item, type KhoState, type NormLine, type Service, type StockDoc, type Supplier, type Warehouse,
} from './khoLogic';

/**
 * Dữ liệu demo kho thẩm mỹ viện — sinh theo "bây giờ" để hạn dùng / cảnh báo luôn có ý nghĩa.
 * Các phiếu xuất được phân lô bằng chính FEFO của hệ thống nên tồn không bao giờ âm.
 */

const D = 86400000;
const H = 3600000;

export const PRACTITIONERS = ['BS. Lan Anh', 'BS. Quốc Bảo', 'KTV Thu Trang'];
/** Người được duyệt chuyên môn thuốc kiểm soát */
export const EXPERTS = ['BS. Lan Anh', 'BS. Quốc Bảo', 'ĐD trưởng Phạm Thu Hà'];

const WAREHOUSES: Warehouse[] = [
  { id: 'wh-central', code: 'KT01', name: 'Kho tổng', kind: 'central', branch: 'Văn phòng', keeper: 'Nguyễn Thị Hạnh' },
  { id: 'wh-q1-room', code: 'TQ1', name: 'Tủ thuốc phòng thủ thuật Q1', kind: 'room', branch: 'Quận 1', keeper: 'ĐD trưởng Phạm Thu Hà' },
  { id: 'wh-q2', code: 'KQ2', name: 'Kho chi nhánh Quận 2', kind: 'branch', branch: 'Quận 2', keeper: 'Trần Văn Lộc' },
  { id: 'wh-retail', code: 'QMP', name: 'Quầy mỹ phẩm Q1', kind: 'retail', branch: 'Quận 1', keeper: 'Lê Mỹ Duyên' },
];

const SUPPLIERS: Supplier[] = [
  { id: 'sup-1', name: 'Công ty CP Dược phẩm Medika', phone: '02838123456' },
  { id: 'sup-2', name: 'NPP Thẩm mỹ Phú Mỹ', phone: '02839876543' },
  { id: 'sup-3', name: 'Thiết bị y tế An Phát', phone: '02436667788' },
  { id: 'sup-4', name: 'Mỹ phẩm La Belle', phone: '0909123456' },
];

const it = (id: string, code: string, name: string, kind: Item['kind'], unit: string, minStock: number, cost: number, extra: Partial<Item> = {}): Item =>
  ({ id, code, name, kind, unit, minStock, cost, active: true, ...extra });

const ITEMS: Item[] = [
  it('it-botox', 'BTX100', 'Botulinum toxin A 100U (Botox)', 'drug', 'Unit', 300, 45000, { packUnit: 'Lọ', packSize: 100, cold: true, supplierId: 'sup-2', brand: 'Allergan' }),
  it('it-dysport', 'DYS300', 'Botulinum toxin A 300U (Dysport)', 'drug', 'Unit', 300, 12000, { packUnit: 'Lọ', packSize: 300, cold: true, supplierId: 'sup-2', brand: 'Ipsen' }),
  it('it-filler-lips', 'FIL-VL', 'Filler HA Volift 1ml', 'drug', 'Ống', 6, 3200000, { packUnit: 'Hộp', packSize: 2, supplierId: 'sup-2', brand: 'Juvederm' }),
  it('it-filler-deep', 'FIL-VM', 'Filler HA Voluma 1ml', 'drug', 'Ống', 4, 4200000, { packUnit: 'Hộp', packSize: 2, supplierId: 'sup-2', brand: 'Juvederm' }),
  it('it-lido', 'LIDO2', 'Lidocain 2% 2ml', 'drug', 'Ống', 60, 3500, { packUnit: 'Hộp', packSize: 50, controlled: true, supplierId: 'sup-1' }),
  it('it-emla', 'EMLA5', 'Kem gây tê Emla 5% 5g', 'drug', 'Tuýp', 10, 120000, { supplierId: 'sup-1' }),
  it('it-tca', 'TCA20', 'Dung dịch peel TCA 20%', 'drug', 'ml', 60, 25000, { packUnit: 'Chai', packSize: 30, supplierId: 'sup-1' }),
  it('it-needle', 'KIM30G', 'Kim tiêm 30G 13mm', 'consumable', 'Cái', 300, 1200, { packUnit: 'Hộp', packSize: 100, supplierId: 'sup-3' }),
  it('it-cannula', 'CAN25', 'Cannula 25G 50mm', 'consumable', 'Cái', 40, 45000, { packUnit: 'Hộp', packSize: 20, supplierId: 'sup-3' }),
  it('it-glove', 'GTV', 'Găng tay y tế vô trùng', 'consumable', 'Đôi', 200, 4500, { packUnit: 'Hộp', packSize: 50, supplierId: 'sup-3' }),
  it('it-gauze', 'GAC10', 'Gạc vô trùng 10×10', 'consumable', 'Gói', 100, 3000, { packUnit: 'Bịch', packSize: 10, supplierId: 'sup-3' }),
  it('it-alcohol', 'CON70', 'Cồn 70° 500ml', 'consumable', 'Chai', 10, 25000, { supplierId: 'sup-3' }),
  it('it-mask', 'MN-PH', 'Mặt nạ phục hồi sau thủ thuật', 'consumable', 'Miếng', 50, 35000, { packUnit: 'Hộp', packSize: 10, supplierId: 'sup-4' }),
  it('it-dermapen', 'DP-12', 'Đầu kim Dermapen 12 kim', 'consumable', 'Cái', 20, 60000, { packUnit: 'Hộp', packSize: 10, supplierId: 'sup-3' }),
  it('it-hifu', 'HIFU-C', 'Cartridge HIFU 4.5mm', 'tip', 'Shot', 15000, 80, { packUnit: 'Cartridge', packSize: 10000, supplierId: 'sup-3' }),
  it('it-ulthera', 'ULT-T', 'Đầu tip Ultherapy 4.5mm', 'tip', 'Line', 1000, 6000, { packUnit: 'Đầu tip', packSize: 2400, supplierId: 'sup-3' }),
  it('it-serum', 'SR-HA', 'Serum cấp ẩm HA 30ml', 'cosmetic', 'Chai', 25, 280000, { price: 650000, supplierId: 'sup-4' }),
  it('it-sunscreen', 'KCN50', 'Kem chống nắng SPF50+ 50ml', 'cosmetic', 'Tuýp', 30, 190000, { price: 450000, supplierId: 'sup-4' }),
  it('it-repair', 'KPH-B5', 'Kem phục hồi B5 40ml', 'cosmetic', 'Tuýp', 20, 150000, { price: 390000, supplierId: 'sup-4' }),
  it('it-tool', 'DC-TP', 'Bộ dụng cụ tiểu phẫu inox', 'tool', 'Bộ', 0, 1500000, { supplierId: 'sup-3' }),
];

const SERVICES: Service[] = [
  { id: 'sv-botox-jaw', name: 'Botox gọn hàm', price: 4500000 },
  { id: 'sv-botox-wrinkle', name: 'Botox xóa nhăn trán', price: 3500000 },
  { id: 'sv-filler-lips', name: 'Filler môi', price: 6500000 },
  { id: 'sv-filler-chin', name: 'Filler cằm', price: 12000000 },
  { id: 'sv-hifu', name: 'HIFU nâng cơ toàn mặt', price: 5000000 },
  { id: 'sv-ulthera', name: 'Ultherapy nâng cơ', price: 35000000 },
  { id: 'sv-peel', name: 'Peel da TCA', price: 1500000 },
  { id: 'sv-dermapen', name: 'Lăn kim tế bào gốc', price: 2500000 },
  { id: 'sv-acne', name: 'Lấy nhân mụn chuẩn y khoa', price: 600000 },
];

const n = (itemId: string, qty: number): NormLine => ({ itemId, qty });
const NORMS: Record<string, NormLine[]> = {
  'sv-botox-jaw': [n('it-botox', 50), n('it-needle', 2), n('it-glove', 1), n('it-gauze', 2)],
  'sv-botox-wrinkle': [n('it-botox', 20), n('it-needle', 2), n('it-glove', 1), n('it-gauze', 2)],
  'sv-filler-lips': [n('it-filler-lips', 1), n('it-lido', 1), n('it-cannula', 1), n('it-needle', 1), n('it-glove', 1), n('it-gauze', 3)],
  'sv-filler-chin': [n('it-filler-deep', 2), n('it-lido', 1), n('it-cannula', 1), n('it-glove', 1), n('it-gauze', 3)],
  'sv-hifu': [n('it-hifu', 800), n('it-glove', 1), n('it-mask', 1)],
  'sv-ulthera': [n('it-ulthera', 400), n('it-glove', 1), n('it-mask', 1)],
  'sv-peel': [n('it-tca', 3), n('it-glove', 1), n('it-gauze', 4), n('it-mask', 1)],
  'sv-dermapen': [n('it-dermapen', 1), n('it-glove', 1), n('it-mask', 1)],
  'sv-acne': [n('it-needle', 2), n('it-glove', 1), n('it-gauze', 5), n('it-mask', 1)],
};

const FALLBACK_CUSTOMERS = [
  { name: 'Nguyễn Thu Hằng', phone: '0901234567' }, { name: 'Trần Mỹ Linh', phone: '0912345678' },
  { name: 'Lê Ngọc Ánh', phone: '0938765432' }, { name: 'Phạm Bảo Ngọc', phone: '0987654321' },
  { name: 'Võ Thanh Tâm', phone: '0977111222' }, { name: 'Đặng Minh Thư', phone: '0966333444' },
  { name: 'Hoàng Gia Hân', phone: '0355666777' }, { name: 'Bùi Khánh Vy', phone: '0388999000' },
];

/** Tăng khi bổ sung dữ liệu demo — trình duyệt đang giữ demo cũ (chưa có phiếu tự tạo) sẽ tự nạp lại */
export const DEMO_VERSION = 4;

export function demoState(now: number, by = 'Admin', customers = FALLBACK_CUSTOMERS): KhoState {
  const cs = customers.length ? customers : FALLBACK_CUSTOMERS;
  let s: KhoState = { v: 1, seededAt: now, demoVersion: DEMO_VERSION, items: ITEMS, warehouses: WAREHOUSES, suppliers: SUPPLIERS, services: SERVICES, norms: NORMS, docs: [] };
  let seq = 0;
  const lid = () => `demo-l${++seq}`;
  const exp = (days: number) => dayjs(now).add(days, 'day').format('YYYY-MM-DD');
  const cost = (id: string) => ITEMS.find(i => i.id === id)!.cost;

  const push = (type: DocType, at: number, d: Partial<StockDoc> & { lines: DocLine[] }, status: StockDoc['status'] = 'approved', who = by) => {
    const doc: StockDoc = {
      id: `demo-${type}-${s.docs.length + 1}`, code: nextCode(s, type, at), type, status, date: at, warehouseId: 'wh-central', createdBy: who, createdAt: at,
      ...(status === 'approved' ? { approvedBy: by, approvedAt: at + H } : {}),
      log: [{ at, by: who, text: 'Tạo phiếu' }, ...(status === 'approved' ? [{ at: at + H, by, text: 'Duyệt phiếu' }] : status === 'pending' ? [{ at: at + 60000, by: who, text: 'Gửi duyệt' }] : [])],
      ...d,
    };
    s = { ...s, docs: [...s.docs, doc] };
    return doc;
  };
  type ImpLine = [itemId: string, lot: string, expDays: number | null, qty: number, unitCost?: number, docQty?: number];
  const imp = (daysAgo: number, supplierId: string, ref: string, lines: ImpLine[], status: StockDoc['status'] = 'approved', extra: Partial<StockDoc> = {}) =>
    push('import', now - daysAgo * D, {
      supplierId, refCode: ref, warehouseId: 'wh-central', ...extra,
      lines: lines.map(([itemId, lotNo, e, qty, c, docQty]) => ({ id: lid(), itemId, lotNo, expiry: e === null ? undefined : exp(e), qty, unitCost: c ?? cost(itemId), docQty: docQty ?? qty })),
    }, status);
  /** Xuất theo FEFO trên chính dữ liệu đang sinh */
  const fefo = (warehouseId: string, req: [string, number][], at: number, allowExpired = false) =>
    req.flatMap(([itemId, qty]) => allocateFefo(s, warehouseId, itemId, qty, { now: at, allowExpired }).lines.map(l => ({ ...l, id: lid() })));
  const xfer = (daysAgo: number, to: string, req: [string, number][], received = true, from = 'wh-central') => {
    const at = now - daysAgo * D;
    const doc = push('transfer', at, { warehouseId: from, toWarehouseId: to, note: 'Lĩnh vật tư định kỳ', lines: fefo(from, req, at) });
    if (received) {
      const r = { ...doc, receivedAt: at + 3 * H, receivedBy: WAREHOUSES.find(w => w.id === to)!.keeper, log: [...doc.log, { at: at + 3 * H, by: WAREHOUSES.find(w => w.id === to)!.keeper, text: 'Xác nhận đã nhận đủ hàng' }] };
      s = { ...s, docs: s.docs.map(d => (d.id === doc.id ? r : d)) };
    }
    return doc;
  };

  // ---- Nhập mua về kho tổng ----
  imp(85, 'sup-2', 'HD-PM-0812', [
    ['it-botox', 'BTX-C4521', 50, 1000, 45000],
    ['it-dysport', 'DYS-7781', 300, 900, 12000],
    ['it-filler-lips', 'VL-2309', 25, 6, 3200000],
    ['it-filler-deep', 'VM-1188', 420, 6, 4200000],
  ]);
  imp(84, 'sup-1', 'HD-MDK-5521', [
    ['it-lido', 'LD-0623', -5, 20, 3200],
    ['it-lido', 'LD-1124', 200, 100, 3500],
    ['it-emla', 'EM-551', 180, 20, 120000],
    ['it-tca', 'TCA-889', 120, 90, 25000],
  ]);
  imp(83, 'sup-3', 'HD-AP-3390', [
    ['it-needle', 'KN-2404', 700, 1000, 1200],
    ['it-cannula', 'CN-112', 600, 40, 45000],
    ['it-glove', 'GT-0912', 500, 500, 4500],
    ['it-gauze', 'GC-3301', 400, 300, 3000],
    ['it-alcohol', 'C70-A', 300, 24, 25000],
    ['it-dermapen', 'DP-091', 365, 30, 60000],
    ['it-hifu', 'HF-5521', 900, 20000, 80],
    ['it-ulthera', 'UT-4401', 700, 4800, 6000],
    ['it-tool', 'DC-2026', null, 3, 1500000],
  ]);
  imp(82, 'sup-4', 'HD-LB-7711', [
    ['it-mask', 'MN-0815', 90, 100, 35000],
    ['it-serum', 'SR-2211', 40, 30, 280000],
    ['it-sunscreen', 'KCN-1402', 300, 60, 190000],
    ['it-repair', 'KPH-551', 250, 40, 150000],
  ]);
  imp(20, 'sup-2', 'HD-PM-0930', [
    ['it-botox', 'BTX-D1102', 400, 500, 46000],
    ['it-filler-lips', 'VL-2405', 500, 10, 3300000, 10],
  ]);

  imp(38, 'sup-2', 'HD-PM-0906', [['it-botox', 'BTX-C4600', 380, 300, 45000], ['it-ulthera', 'UT-4402', 800, 2400, 6000]]);
  // Bổ sung vật tư giữa kỳ (để so sánh nhập kỳ này / kỳ trước, giá kim nhập giảm nhẹ)
  imp(45, 'sup-3', 'HD-AP-3455', [
    ['it-needle', 'KN-2408', 720, 300, 1150],
    ['it-glove', 'GT-0930', 520, 200, 4500],
  ]);

  // ---- Lĩnh về tủ thuốc Q1, quầy mỹ phẩm ----
  xfer(80, 'wh-q1-room', [
    ['it-botox', 800], ['it-filler-lips', 6], ['it-filler-deep', 6], ['it-lido', 40], ['it-emla', 8], ['it-tca', 60],
    ['it-needle', 500], ['it-cannula', 30], ['it-glove', 200], ['it-gauze', 200], ['it-mask', 60], ['it-dermapen', 20],
    ['it-hifu', 20000], ['it-ulthera', 4800],
  ]);
  xfer(79, 'wh-retail', [['it-serum', 30], ['it-sunscreen', 50], ['it-repair', 35]]);
  xfer(18, 'wh-q1-room', [['it-botox', 300], ['it-filler-lips', 4]]);

  // ---- Kỳ trước (31–75 ngày trước): ít buổi hơn, dùng đúng định mức ----
  const older: [string, number][] = [
    ['sv-botox-jaw', 74], ['sv-hifu', 72], ['sv-acne', 70], ['sv-botox-wrinkle', 66], ['sv-peel', 63], ['sv-hifu', 60],
    ['sv-botox-jaw', 58], ['sv-botox-jaw', 57], ['sv-hifu', 56], ['sv-ulthera', 54], ['sv-filler-lips', 52], ['sv-botox-wrinkle', 50],
    ['sv-hifu', 48], ['sv-botox-jaw', 47], ['sv-acne', 45], ['sv-hifu', 43], ['sv-peel', 41], ['sv-botox-jaw', 40], ['sv-ulthera', 38],
    ['sv-dermapen', 36], ['sv-botox-jaw', 35], ['sv-acne', 34], ['sv-hifu', 33], ['sv-botox-wrinkle', 32], ['sv-hifu', 31],
  ];
  older.forEach(([serviceId, daysAgo], i) => {
    const at = now - daysAgo * D - (3 + (i % 4)) * H;
    const practitioner = serviceId === 'sv-acne' || serviceId === 'sv-peel' ? PRACTITIONERS[2] : PRACTITIONERS[i % 2];
    push('issue_service', at, {
      warehouseId: 'wh-q1-room', serviceId, practitioner, customer: cs[(i + 5) % cs.length],
      lines: fefo('wh-q1-room', NORMS[serviceId].map(x => [x.itemId, x.qty] as [string, number]), at),
    }, 'approved', 'ĐD trưởng Phạm Thu Hà');
  });

  // ---- Buổi dịch vụ 30 ngày qua tại phòng thủ thuật Q1 ----
  const plan: [string, number][] = [
    ['sv-botox-jaw', 29], ['sv-hifu', 28], ['sv-filler-lips', 27], ['sv-acne', 26], ['sv-botox-wrinkle', 25], ['sv-peel', 24],
    ['sv-hifu', 23], ['sv-botox-jaw', 22], ['sv-ulthera', 21], ['sv-filler-chin', 20], ['sv-dermapen', 19], ['sv-hifu', 18],
    ['sv-botox-jaw', 16], ['sv-acne', 15], ['sv-hifu', 14], ['sv-filler-lips', 13], ['sv-botox-wrinkle', 12], ['sv-hifu', 11],
    ['sv-peel', 10], ['sv-hifu', 9], ['sv-botox-jaw', 8], ['sv-ulthera', 7], ['sv-hifu', 6], ['sv-filler-chin', 5],
    ['sv-dermapen', 4], ['sv-hifu', 3], ['sv-botox-jaw', 2], ['sv-hifu', 2], ['sv-acne', 1], ['sv-botox-wrinkle', 1],
    ['sv-hifu', 1], ['sv-hifu', 0],
  ];
  plan.forEach(([serviceId, daysAgo], i) => {
    const at = now - daysAgo * D - (3 + (i % 5)) * H;
    const practitioner = serviceId === 'sv-acne' || serviceId === 'sv-peel' ? PRACTITIONERS[2] : PRACTITIONERS[i % 2];
    // BS. Quốc Bảo thường dùng botox / HIFU vượt định mức ~20% → hiện ở báo cáo chênh lệch
    const over = practitioner === 'BS. Quốc Bảo' && (serviceId.startsWith('sv-botox') || serviceId === 'sv-hifu') ? 1.2 : 1;
    const req = NORMS[serviceId].map(x => [x.itemId, x.itemId === 'it-botox' || x.itemId === 'it-hifu' ? Math.round(x.qty * over) : x.qty] as [string, number]);
    push('issue_service', at, {
      warehouseId: 'wh-q1-room', serviceId, practitioner, customer: cs[i % cs.length],
      lines: fefo('wh-q1-room', req, at),
    }, 'approved', 'ĐD trưởng Phạm Thu Hà');
  });

  // ---- Bán mỹ phẩm tại quầy ----
  [[26, 'it-serum', 2], [21, 'it-sunscreen', 3], [17, 'it-repair', 2], [12, 'it-serum', 3], [9, 'it-sunscreen', 4], [6, 'it-serum', 4], [3, 'it-repair', 3], [1, 'it-sunscreen', 2]]
    .forEach(([daysAgo, itemId, qty], i) => {
      const at = now - (daysAgo as number) * D - 2 * H;
      push('issue_sale', at, { warehouseId: 'wh-retail', customer: cs[(i + 3) % cs.length], lines: fefo('wh-retail', [[itemId as string, qty as number]], at) }, 'approved', 'Lê Mỹ Duyên');
    });

  [[70, 'it-sunscreen', 2], [55, 'it-serum', 1], [44, 'it-repair', 1], [35, 'it-sunscreen', 2]].forEach(([daysAgo, itemId, qty], i) => {
    const at = now - (daysAgo as number) * D - 2 * H;
    push('issue_sale', at, { warehouseId: 'wh-retail', customer: cs[(i + 1) % cs.length], lines: fefo('wh-retail', [[itemId as string, qty as number]], at) }, 'approved', 'Lê Mỹ Duyên');
  });

  // ---- Hủy, kiểm kho đã xong ----
  push('dispose', now - 40 * D, { warehouseId: 'wh-q1-room', reason: 'Hỏng / vỡ', note: 'Rơi vỡ 1 ống khi chuẩn bị thủ thuật', lines: fefo('wh-q1-room', [['it-filler-lips', 1]], now - 40 * D) });
  // Kiểm quầy mỹ phẩm 10 ngày trước: thiếu 1 tuýp kem chống nắng (tồn sổ sách tính tại thời điểm kiểm)
  const countAt = now - 10 * D;
  const shelf = lotsAt(s, countAt).filter(l => l.warehouseId === 'wh-retail' && l.qty > 0);
  if (shelf.length) {
    push('count', countAt, {
      warehouseId: 'wh-retail', note: 'Kiểm kho cuối tháng quầy mỹ phẩm',
      lines: shelf.map(l => ({ id: lid(), itemId: l.itemId, lotNo: l.lotNo, expiry: l.expiry, qty: 0, unitCost: l.unitCost, systemQty: l.qty, counted: l.itemId === 'it-sunscreen' ? l.qty - 1 : l.qty })),
    }, 'approved', 'Lê Mỹ Duyên');
  }

  // ---- Việc đang dở: chờ duyệt, đang chuyển, nháp, bị từ chối ----
  imp(1, 'sup-3', 'HD-AP-3512', [['it-glove', 'GT-1010', 540, 300, 4400, 300], ['it-gauze', 'GC-3410', 420, 200, 3000, 220]], 'pending', { note: 'Thiếu 20 gói gạc so với hóa đơn — đã báo NCC giao bù' });
  imp(4, 'sup-2', 'HD-PM-1002', [['it-filler-deep', 'VM-1290', 400, 4, 4600000]], 'rejected', { rejectReason: 'Đơn giá cao hơn hợp đồng (4.200.000 ₫) — đề nghị NCC xuất lại hóa đơn' });
  push('issue_service', now - 2 * H, {
    warehouseId: 'wh-q1-room', serviceId: 'sv-filler-lips', practitioner: PRACTITIONERS[0], customer: cs[2],
    lines: fefo('wh-q1-room', NORMS['sv-filler-lips'].map(x => [x.itemId, x.qty] as [string, number]), now),
  }, 'pending', 'ĐD trưởng Phạm Thu Hà');
  push('dispose', now - 5 * H, { warehouseId: 'wh-q1-room', reason: 'Sai nhiệt độ bảo quản', note: 'Tủ mát mất điện 3 giờ', lines: fefo('wh-q1-room', [['it-mask', 2]], now) }, 'pending', 'ĐD trưởng Phạm Thu Hà');
  xfer(0.3, 'wh-q2', [['it-botox', 200], ['it-needle', 100], ['it-glove', 50], ['it-mask', 10]], false);
  push('transfer', now - 30 * 60000, { warehouseId: 'wh-central', toWarehouseId: 'wh-retail', note: 'Bổ sung quầy', lines: fefo('wh-central', [['it-repair', 5]], now) }, 'draft');

  return s;
}
